import { useTheme } from '@emotion/react';
import { Button, CircularProgress, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { RenderIf } from '../../../../utils/customHooks';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import FileViewer from '../../../../components/FileViewer/FileViewer';

const statusList = [
  {
    countryId: '1',
    countryName: 'InProgress',
  },
  {
    countryId: '2',
    countryName: 'Approved',
  },
  {
    countryId: '3',
    countryName: 'Rejected',
  },
];

const screeningStatus = {
  pending: '#BA9B1D',
  approved: '#00A63226',
  rejected: '#B11B1B',
  inprogress: '#006DFF',
  pendingColor: '#FFF',
  approvedColor: '#00A632',
  rejectedColor: '#fff',
  inprogressColor: '#fff',
};

export default function CompanyDetails({ details, isLoading, type }) {
  const theme = useTheme();
  const wordsPerLine = 93; // Number of words per line
  const totalWords = 3; // Total words in driverData?.blockNotes
  const totalLines = Math.ceil(totalWords / wordsPerLine); // Calculate the total number of lines
  const minHeight = `${160 + totalLines * 10}px`; // Calculate the minHeight based on the number of lines

  const [status, setStatus] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleDownload = async (file) => {
    try {
      const response = await fetch(file);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const name = file?.split('/').pop();
      link.download = `${name}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return isLoading ? (
    <Stack
      style={{ backgroundColor: '#FFFFFF' }}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <CircularProgress />
    </Stack>
  ) : (
    <Stack
      flex={1}
      style={{ backgroundColor: '#FFFFFF' }}
    >
      {details?.status === '2' ? (
        <>
          <Stack
            style={{ backgroundColor: '#FEF1ED', minHeight: minHeight, padding: '20px', borderRadius: '4px' }}
            className="mx-[24px] mt-[25px]"
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                className="!flex"
                direction="row"
              >
                <ErrorIcon sx={{ color: '#B11B1B', fontSize: '18px' }} />
                <Typography
                  style={{ color: '#B11B1B', fontSize: '14px', paddingLeft: '10px' }}
                  className="ff-Roboto fw-bold"
                >
                  {' '}
                  Blocked on {moment(details?.blockHistory?.blockedDate).format('DD MMM YYYY')} for reason
                </Typography>
              </Stack>
            </Stack>
            <Stack>
              <Typography
                style={{ color: '#B11B1B', fontSize: '12px', marginTop: '15px', borderTopWidth: 1, paddingTop: '10px' }}
                className="ff-Roboto fw-regular"
              >
                {details?.blockHistory?.blockedReason}
              </Typography>
            </Stack>
          </Stack>
        </>
      ) : null}

      <Stack
        direction="row"
        className="p-[25px] pt-2 pb-0"
      >
        <Stack flex={0.6}>
          <Stack>
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Display Name
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto !mb-[16px]"
              color={'#14285A'}
            >
              {details?.displayName || '-'}
            </Typography>
          </Stack>
        </Stack>
        <Stack flex={0.2}>
        <Stack className='ml-[2px]'>
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Client Type
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto capitalize"
              color={'#14285A'}
            >
              {details?.clientType === 'cwclient' ? 'CW Client' : details?.clientType === 'bossclient' ? 'BOSS Client' : '-'}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        className="p-[25px] pt-0"
        flex={1}
      >
        <Stack flex={0.8}>
          <Stack>
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Company Name
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.companyName || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Legal Entity type
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto capitalize"
              color={'#14285A'}
            >
              {details?.entityType || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Address
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.regAddress || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              No of Vessels
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details.noOfVessals || '0'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Last Modified
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {moment(new Date(details?.modifiedOn)).format('DD MMM YYYY') || '-'}
            </Typography>
          </Stack>
        </Stack>
        <Stack flex={0.2}>
          <Stack>
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Company Type
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto capitalize"
              color={'#14285A'}
            >
              {details?.companyType || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px] !w-[200px] overflow-hidden">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Company website
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.companyWebsite || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              State/Country
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
              style={{ textTransform: 'capitalize' }}
            >
              {details?.stateName || '-'}
              {', '}
              {details?.countryName || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Access To Trader's List
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.traderListAccess === '1' ? 'Yes' : 'No'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              EUA Trading
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.euaTrading ? 'Yes' : 'No'}
            </Typography>
          </Stack>
          {/* <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Screening Status
            </Typography>
            <Stack>
              <TextField
                style={{}}
                fullWidth
                id={'ordertype'}
                name="orderType"
                requiredMark={true}
                select
                sx={{ marginBottom: '25px !important', width: '90px !important', height: '30px !important', backgroundColor: 'red !important' }}
                onChange={(e) => {import FileViewer from './../../../../components/FileViewer/FileViewer';

                  setStatus(e.target.value);
                }}
              >
                {statusList?.map((item) => (
                  <MenuItem
                    key={item.countryId}
                    value={item.countryName}
                  >
                    {item.countryName}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack> */}
        </Stack>
      </Stack>
      {/* <Stack>
        {details?.traderDetails && details?.traderDetails?.length > 0 && (
          <Stack
            direction="row"
            style={{ backgroundColor: '#006DFF0D', height: '48px', alignItems: 'center' }}
            className="px-[25px]"
          >
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.2}
              className="fw-medium fs-12 ff-Roboto"
            >
              #
            </Typography>
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.5}
              className="fw-medium fs-12 ff-Roboto"
            >
              Trader
            </Typography>
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.3}
              className="fw-medium fs-12 ff-Roboto"
            >
              Screening Status
            </Typography>
          </Stack>
        )}
        {(details?.traderDetails || []).map((item, index) => (
          <Stack
            key={item._id}
            direction="row"
            style={{ backgroundColor: '#FFFFFF', height: '48px', alignItems: 'center', borderBottomColor: theme.palette.border.main, borderBottomWidth: '0.1px' }}
            className="px-[25px]"
          >
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.2}
              className="fw-regular fs-12 ff-Roboto"
            >
              {index + 1}
            </Typography>
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.5}
              className="fw-regular fs-12 ff-Roboto"
            >
              {item?.traderName}
            </Typography>
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.3}
              className="fw-regular fs-12 ff-Roboto"
            >
              <Stack justifyContent={'center'} alignItems={'center'} className='fw-regular fs-12 ff-Roboto h-[28px] px-[10px] rounded-[4px] capitalize' sx={{backgroundColor: screeningStatus[item?.screeningStatus], color: screeningStatus[`${item?.screeningStatus}Color`]}}>
              {item?.screeningStatus}
              </Stack>
            </Typography>
          </Stack>
        ))}
      </Stack> */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: '25px' }}
        flex={1}
      >
        <Stack flex={0.65}>
          <Stack style={{ maxWidth: '150px' }}>
            <Typography
              className="fw-regular fs-12 ff-Roboto capitalize"
              color={'#14285A'}
            >
              Admin Name
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.name || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Email Id
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.email || '-'}
            </Typography>
          </Stack>
        </Stack>
        <Stack flex={0.4}>
          <Stack>
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Designation
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto capitalize"
              color={'#14285A'}
            >
              {details?.designation || '-'}
            </Typography>
          </Stack>
          <Stack className="mt-[16px]">
            <Typography
              className="fw-regular fs-12 ff-Roboto"
              color={'#14285A'}
            >
              Phone No
            </Typography>
            <Typography
              className="fw-medium fs-14 ff-Roboto"
              color={'#14285A'}
            >
              {details?.phoneCode || ''}
              {details?.phoneNumber || '-'}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        style={{ backgroundColor: theme.palette.border.main, height: '1px', marginBottom: '10px', opacity: 0.5 }}
        className="mx-[25px]"
      />
      {details?.documents?.length > 0 && (
        <Stack>
          <Stack
            direction="row"
            style={{ backgroundColor: '#006DFF0D', height: '48px', alignItems: 'center' }}
            className="px-[25px]"
          >
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.8}
              className="fw-medium fs-12 ff-Roboto"
            >
              Attachment
            </Typography>
            <Typography
              style={{ color: theme.palette.primary.text }}
              flex={0.2}
              className="fw-medium fs-12 ff-Roboto"
            >
              Action
            </Typography>
          </Stack>
          {details?.documents?.map((doc, index) => (
            <>
              {doc?.attachment && (
                <Stack
                  key={index}
                  direction="row"
                  style={{ backgroundColor: '#FFFFFF', height: '48px', alignItems: 'center', borderBottomColor: theme.palette.border.main, borderBottomWidth: '0.1px' }}
                  className="px-[25px]"
                >
                  <Typography
                    style={{ color: theme.palette.primary.text }}
                    flex={0.8}
                    className="fw-regular fs-12 ff-Roboto"
                  >
                    {doc?.certificateName}
                  </Typography>
                  {doc?.attachment && (
                    <Stack
                      direction={'row'}
                      flex={0.4}
                      justifyContent="center"
                    >
                      <Tooltip
                        disableInteractive
                        title="View"
                      >
                        <IconButton
                          onClick={() => {
                            setFilePreview(doc?.attachment);
                            setOpenModal(true);
                          }}
                        >
                          <VisibilityIcon
                            style={{
                              color: theme.palette.primary.light,
                              transform: 'scale(0.689)',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        disableInteractive
                        title="Download"
                      >
                        <IconButton onClick={() => handleDownload(doc?.attachment)}>
                          <DownloadIcon
                            style={{
                              color: theme.palette.primary.light,
                              transform: 'scale(0.689)',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              )}
            </>
          ))}
        </Stack>
      )}
      {openModal && (
        <FileViewer
          file={filePreview}
          closeModal={() => setOpenModal(false)}
          open={openModal}
          disableDownload={true}
        />
      )}
    </Stack>
  );
}
